import { createElement } from '../../../core/dom/helpers.js';
import { createReorderControls } from '../../../core/dom/reorder-controls.js';
import { deriveFileKind } from '../../../core/media/file-metadata.js';
import { attachImageFallback, setImageAlt } from '../../../core/media/image-fallback.js';

/**
 * @typedef {import('../../../contracts/upload.js').UploadValue} UploadValue
 * @typedef {import('../../../contracts/upload.js').ExistingUpload} ExistingUpload
 * @typedef {import('../../../contracts/upload.js').NewUpload} NewUpload
 */

/**
 * Is this upload value an image? Unambiguous order: explicit `fileKind` first, then derive from the
 * name (+ mimeType), then from the url's extension. `new` items use the File's name/type. Returns false
 * for anything unrecognized → the card shows a filename chip instead of a thumbnail (the editor stays).
 * @param {UploadValue} value
 */
function valueIsImage(value) {
  if (value.kind === 'new') {
    return value.file.type.startsWith('image/') || deriveFileKind(value.file.name) === 'image';
  }
  if (value.fileKind === 'image') return true;
  if (value.fileKind) return false; // explicit non-image kind
  const byName = deriveFileKind(value.name ?? '', value.mimeType);
  if (byName) return byName === 'image';
  // Fall back to the url's extension (path may carry it even when name doesn't).
  return deriveFileKind(value.url ?? '') === 'image';
}

/** @param {UploadValue} value @returns {string} */
function displayName(value) {
  if (value.kind === 'new') return value.file.name;
  return value.name ?? value.url ?? '';
}

/**
 * ONE reusable photo card — thumb + Photo Title + Photo Description + corner × remove, optionally with a
 * drag handle (multi/gallery). Used by BOTH the single and multi file-driver modes so the card markup +
 * wiring lives in one place (gallery = this card × N + reorder).
 *
 * Callbacks are KEYED, not identity-based: an edit replaces the value object (reference-stability
 * invariant), so the mount fn resolves the current index by `key` at call time — the card never does its
 * own array lookup. Disabled state is imperative (`setDisabled`), driven by the driver from the session.
 *
 * Reorder (multi/gallery): drag the handle (mouse) OR the keyboard/touch-friendly Move up /
 * Move down buttons. The buttons are the ACCESSIBLE path — drag/drop is weak on touch and
 * unusable by keyboard. They render only when `draggable` AND both move callbacks are provided;
 * `setMoveState` disables each at the list boundary; `focusMove` re-homes focus after a reorder.
 *
 * @param {Object} args
 * @param {string} args.key - Stable client key the callbacks echo back.
 * @param {UploadValue} args.value
 * @param {boolean} [args.draggable] - When true (multi), render a drag handle (the only draggable element).
 * @param {(key: string, text: string) => void} args.onTitleChange
 * @param {(key: string, text: string) => void} args.onDescriptionChange
 * @param {(key: string) => void} args.onRemove
 * @param {(key: string) => void} [args.onMoveUp] - Multi only: move this card one position earlier.
 * @param {(key: string) => void} [args.onMoveDown] - Multi only: move this card one position later.
 * @returns {{ el: HTMLElement, handle: HTMLElement | null, setDisabled: (d: boolean) => void, setMoveState: (canUp: boolean, canDown: boolean) => void, focusMove: (preferUp: boolean) => void, destroy: () => void }}
 */
export function createImageCard({ key, value, draggable = false, onTitleChange, onDescriptionChange, onRemove, onMoveUp, onMoveDown }) {
  let disabled = false;
  /** @type {string | null} */
  let objectUrl = null;

  const card = createElement('div', { class: 'file-driver__card' });

  // Thumb (image) or filename chip (non-image fallback — the editor still renders).
  const thumbWrap = createElement('div', { class: 'file-driver__card-thumb' });
  /** @type {HTMLImageElement | null} — referenced by the live alt binding when an image. */
  let imgEl = null;
  if (valueIsImage(value)) {
    imgEl = /** @type {HTMLImageElement} */ (createElement('img', { class: 'file-driver__card-img' }));
    if (value.kind === 'existing') {
      attachImageFallback(imgEl); // one-shot fallback BEFORE src (a 404'd url else shows a broken icon)
      imgEl.src = value.url;
    } else {
      objectUrl = URL.createObjectURL(value.file);
      imgEl.src = objectUrl;
    }
    imgEl.alt = value.title ?? '';
    thumbWrap.appendChild(imgEl);
  } else {
    const chip = createElement('span', { class: 'file-driver__card-filename' });
    chip.textContent = displayName(value);
    thumbWrap.appendChild(chip);
  }

  // Fields column: Title input + Description textarea.
  const fields = createElement('div', { class: 'file-driver__card-fields' });

  const titleId = `imgcard-title-${key}`;
  const titleLabel = createElement('label', { class: 'file-driver__card-label', for: titleId }, ['Photo title']);
  const titleInput = /** @type {HTMLInputElement} */ (createElement('input', {
    type: 'text', id: titleId, class: 'file-driver__card-title', placeholder: 'Photo title',
  }));
  titleInput.value = value.title ?? '';
  titleInput.addEventListener('input', () => {
    if (disabled) return;
    // Live alt — via setImageAlt so a failed image keeps its "(image unavailable)"
    // status instead of having it erased by the new title.
    if (imgEl) setImageAlt(imgEl, titleInput.value);
    onTitleChange(key, titleInput.value);
  });

  const descId = `imgcard-desc-${key}`;
  const descLabel = createElement('label', { class: 'file-driver__card-label', for: descId }, ['Photo description']);
  const descInput = /** @type {HTMLTextAreaElement} */ (createElement('textarea', {
    id: descId, class: 'file-driver__card-desc', rows: '2', placeholder: 'Photo description',
  }));
  descInput.value = value.description ?? '';
  descInput.addEventListener('input', () => {
    if (disabled) return;
    onDescriptionChange(key, descInput.value);
  });

  fields.appendChild(titleLabel);
  fields.appendChild(titleInput);
  fields.appendChild(descLabel);
  fields.appendChild(descInput);

  // Corner × remove (top-right).
  const removeBtn = /** @type {HTMLButtonElement} */ (createElement('button', {
    type: 'button', class: 'file-driver__card-remove', 'aria-label': 'Remove photo',
  }, ['×']));
  removeBtn.addEventListener('click', () => {
    if (disabled) return;
    onRemove(key);
  });

  // Reorder controls (multi only): a drag handle (mouse) + Move up/down buttons (keyboard/touch).
  // Handle-and-buttons are INDEPENDENT: a draggable card renders its handle always, but the ▲/▼
  // buttons only when BOTH move callbacks are supplied (a drag-only card keeps drag, no buttons).
  const reorder = draggable
    ? createReorderControls({
        key, ariaNoun: 'photo', orientation: 'column',
        withHandle: true, withMoveButtons: !!onMoveUp && !!onMoveDown,
        onMoveUp, onMoveDown,
      })
    : null;
  const handle = reorder ? reorder.handle : null;
  if (reorder) card.appendChild(reorder.el);

  card.appendChild(thumbWrap);
  card.appendChild(fields);
  card.appendChild(removeBtn);

  return {
    el: card,
    handle,
    /** @param {boolean} d */
    setDisabled(d) {
      disabled = d;
      titleInput.disabled = d;
      descInput.disabled = d;
      removeBtn.disabled = d;
      reorder?.setDisabled(d); // toggles handle draggable + move-button disabled
      card.classList.toggle('file-driver__card--disabled', d);
    },
    /** @param {boolean} up @param {boolean} down — whether this card can move up / down (boundary state). */
    setMoveState(up, down) { reorder?.setMoveState(up, down); },
    /** @param {boolean} preferUp — re-home focus onto a Move button after a reorder rebuilt the card. */
    focusMove(preferUp) { reorder?.focusMove(preferUp); },
    destroy() {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
        objectUrl = null;
      }
      reorder?.destroy();
      card.remove();
    },
  };
}
